import {
  type Editor,
  Path,
  type PathRef,
  Point,
  type PointRef,
} from 'domains/reporter/RichTextEditor/core';
import { afterPaint } from 'utils/afterPaint';

/**
 * The live handle an insertion hands back to point at the content it just wrote.
 * Stitching returns a `PointRef`; the picklist replace branch takes a `PathRef`.
 * Slate transforms either against every subsequent operation, so it keeps pointing
 * at the right node as the document changes around it.
 */
export type InsertionAnchorRef = PathRef | PointRef;

interface PendingInsertionPaint {
  anchorRef: InsertionAnchorRef;
  cancelAfterPaint?: () => void;
  editor: Editor;
  hasBeenNotified: boolean;
  onPainted: () => void;
}

const pendingInsertionPaints = new Map<string, PendingInsertionPaint>();

/**
 * A point is not a leaf: we want the containing text node, so `decorate` reduces to a
 * path comparison with no range geometry. Returns null once the content the ref pointed
 * at has been deleted.
 */
function anchorPath(anchorRef: InsertionAnchorRef): Path | null {
  const current = anchorRef.current;
  if (current == null) return null;

  return Point.isPoint(current) ? current.path : current;
}

/**
 * `unref()` is the only thing that removes the ref from the editor's active set. Skipping
 * it leaks a ref that Slate transforms on every operation for the rest of the session.
 */
function releaseInsertionPaint(insertionId: string, pendingInsertionPaint: PendingInsertionPaint) {
  pendingInsertionPaints.delete(insertionId);
  pendingInsertionPaint.anchorRef.unref();
}

export function cancelInsertionPaint(insertionId: string): void {
  const pendingInsertionPaint = pendingInsertionPaints.get(insertionId);
  if (pendingInsertionPaint == null) return;

  releaseInsertionPaint(insertionId, pendingInsertionPaint);
  pendingInsertionPaint.cancelAfterPaint?.();
}

/**
 * Called when an editor unmounts. Nothing else will ever release these: the mark that
 * would have triggered `notifyInsertionMarkMounted` is gone with the editor, so without
 * this the entry sits in the map holding a ref for the rest of the session.
 */
export function cancelInsertionPaintsForEditor(editor: Editor): void {
  for (const [insertionId, pendingInsertionPaint] of pendingInsertionPaints) {
    if (pendingInsertionPaint.editor === editor) cancelInsertionPaint(insertionId);
  }
}

export function registerInsertionPaint({
  editor,
  insertionId,
  anchorRef,
  onPainted,
}: {
  editor: Editor;
  insertionId: string;
  anchorRef: InsertionAnchorRef;
  onPainted: () => void;
}): () => void {
  cancelInsertionPaint(insertionId);

  const nextAnchorPath = anchorPath(anchorRef);
  if (nextAnchorPath != null) {
    // A Slate text leaf can carry only one insertion id. A newer insertion on the same
    // live leaf supersedes an older insertion that never reached its paint callback;
    // retaining the older ref would let it follow later transforms and report a stale,
    // arbitrarily long EditToScreen duration on a future render. Paths are editor-local,
    // so an unrelated editor's [0, 0] is a different leaf and must not supersede.
    for (const [pendingInsertionId, pendingInsertionPaint] of pendingInsertionPaints) {
      if (pendingInsertionPaint.editor !== editor) continue;

      const pendingAnchorPath = anchorPath(pendingInsertionPaint.anchorRef);
      if (pendingAnchorPath != null && Path.equals(pendingAnchorPath, nextAnchorPath)) {
        cancelInsertionPaint(pendingInsertionId);
      }
    }
  }

  const pendingInsertionPaint: PendingInsertionPaint = {
    anchorRef,
    editor,
    hasBeenNotified: false,
    onPainted,
  };
  pendingInsertionPaints.set(insertionId, pendingInsertionPaint);

  return () => {
    if (pendingInsertionPaints.get(insertionId) !== pendingInsertionPaint) return;
    cancelInsertionPaint(insertionId);
  };
}

export function notifyInsertionMarkMounted(insertionId: string): void {
  const pendingInsertionPaint = pendingInsertionPaints.get(insertionId);
  if (pendingInsertionPaint == null || pendingInsertionPaint.hasBeenNotified) return;

  pendingInsertionPaint.hasBeenNotified = true;
  pendingInsertionPaint.cancelAfterPaint = afterPaint(() => {
    if (pendingInsertionPaints.get(insertionId) !== pendingInsertionPaint) return;

    // Content deleted between the mount notification and the paint. Treated as an
    // insertion that never reached the screen: no ScreenUpdate mark and no measure.
    const hasAnchor = anchorPath(pendingInsertionPaint.anchorRef) != null;

    releaseInsertionPaint(insertionId, pendingInsertionPaint);
    if (hasAnchor) pendingInsertionPaint.onPainted();
  });
}

/**
 * Read by `decorate`, which runs per node on every render. Checked first so the common
 * case — nothing pending anywhere — costs a single size comparison. The editor scan past
 * that is bounded by the number of concurrent insertions across all mounted editors.
 */
export function hasPendingInsertionAnchors(editor: Editor): boolean {
  if (pendingInsertionPaints.size === 0) return false;

  for (const pendingInsertionPaint of pendingInsertionPaints.values()) {
    if (pendingInsertionPaint.editor === editor) return true;
  }

  return false;
}

export function findInsertionAnchorId(editor: Editor, path: Path): string | null {
  for (const [insertionId, pendingInsertionPaint] of pendingInsertionPaints) {
    if (pendingInsertionPaint.editor !== editor) continue;

    const currentPath = anchorPath(pendingInsertionPaint.anchorRef);
    if (currentPath != null && Path.equals(currentPath, path)) return insertionId;
  }

  return null;
}
